import React from 'react';
import { Grid, Paper, Typography, Box, Button, ButtonGroup, Skeleton, IconButton, Tooltip } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';

export default function CourseDetailsPanel({ course, onGenerateCover, generatingCovers }) {
  const hasVerticalCover = Boolean(course.vCover);
  const hasHorizontalCover = Boolean(course.hCover);
  const hasThumbnail = Boolean(course.thumbnail); // Add check for thumbnail
  
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Description</Typography>
          <Typography paragraph>
            {course.description || 'No description provided'}
          </Typography>
          
          {course.prerequisite && (
            <>
              <Typography variant="h6" gutterBottom>Prerequisites</Typography>
              <Typography paragraph>{course.prerequisite}</Typography>
            </>
          )}
        </Paper>
      </Grid>
      
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Course Details</Typography>
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">URL Slug</Typography>
            <Typography>{course.slug || 'No slug set'}</Typography>
          </Box>
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">Created</Typography>
            <Typography>{new Date(course.createdAt).toLocaleDateString()}</Typography>
          </Box>
        </Paper>
        
        {/* Add Thumbnail Section */}
        {hasThumbnail && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" gutterBottom>Course Thumbnail</Typography>
            </Box>
            
            <Box 
              component="img"
              src={course.thumbnail}
              alt="Course Thumbnail"
              sx={{ 
                width: '100%', 
                borderRadius: 1,
                border: '1px solid #eee',
                mb: 1
              }}
            />
            
            <Typography variant="caption" color="text.secondary">
              This thumbnail is used for course listings and previews.
            </Typography>
          </Paper>
        )}
        
        {/* Course Covers Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" gutterBottom>Course Covers</Typography>
            
            {/* Replace ButtonGroup with individual icon buttons with tooltips */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Generate vertical cover">
                <IconButton 
                  onClick={() => onGenerateCover('vertical')}
                  disabled={generatingCovers}
                  color="primary"
                  size="small"
                >
                  <VerticalAlignBottomIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Generate horizontal cover">
                <IconButton 
                  onClick={() => onGenerateCover('horizontal')}
                  disabled={generatingCovers}
                  color="primary"
                  size="small"
                >
                  <HorizontalRuleIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Generate both cover types">
                <IconButton 
                  onClick={() => onGenerateCover('both')}
                  disabled={generatingCovers}
                  color="secondary"
                  size="small"
                >
                  <ImageIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {generatingCovers && (
            <Box mb={2}>
              <Typography variant="body2" color="primary" mb={1}>
                Generating course covers with AI...
              </Typography>
              <Skeleton variant="rectangular" height={150} animation="wave" />
            </Box>
          )}
          
          {/* Vertical Cover */}
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              Vertical Cover:
            </Typography>
            {hasVerticalCover ? (
              <Box 
                component="img"
                src={course.vCover}
                alt="Vertical Course Cover"
                sx={{ 
                  width: '100%', 
                  borderRadius: 1,
                  border: '1px solid #eee'
                }}
              />
            ) : (
              <Box 
                sx={{ 
                  height: 200, 
                  bgcolor: 'rgba(0,0,0,0.05)', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No vertical cover
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Horizontal Cover */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Horizontal Cover:
            </Typography>
            {hasHorizontalCover ? (
              <Box 
                component="img"
                src={course.hCover}
                alt="Horizontal Course Cover"
                sx={{ 
                  width: '100%', 
                  borderRadius: 1,
                  border: '1px solid #eee'
                }}
              />
            ) : (
              <Box 
                sx={{ 
                  height: 120, 
                  bgcolor: 'rgba(0,0,0,0.05)', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No horizontal cover
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}
